import {useCallback, useMemo} from 'react';
import {ResourceAllocationState} from '../../../types';
import {getWeekInfo} from '../../../lib/week-calculator';
import {
    INCEPTION_WEEK,
    resolveAsOf
} from '../../../calc_engine/calculators/config-resolver';

type AuthenticatedFetch = (
    url: string,
    options?: RequestInit
) => Promise<Response>;

interface SignalRConnection {
    getConnectionId(): string | null;
}

interface ActionDeps {
    // Ref, not the state value: callbacks read state via stateRef.current so
    // their identities survive state changes — the provider value can then be
    // memoized and downstream memo boundaries hold.
    stateRef: {current: ResourceAllocationState};
    setState: React.Dispatch<React.SetStateAction<ResourceAllocationState>>;
    authenticatedFetch: AuthenticatedFetch;
    signalRService: SignalRConnection;
    customerId: string | undefined;
    baseUrl: string;
}

export interface ConfigWriteOptions {
    // undefined = rewrite history (the change applies to every week, past and
    // future); a global week number = dated write — weeks before it keep the
    // value previously in force. Working hours default to dated (factual
    // record); thresholds/utilisation default to rewrite (interpretive).
    effectiveFromWeek?: number;
}

export type ConfigVersionType =
    | 'working-hours'
    | 'rag-threshold'
    | 'utilisation';

// Optimistic mirror of the server's version-series write: rewrite collapses
// the series to a single inception row; a dated write upserts at that week,
// pinning the previously in-force value at inception on a first-ever save.
function computeNextVersions<T extends {effectiveFromWeek: number}>(
    previousSingle: T,
    previousVersions: T[] | undefined,
    newValues: T,
    effectiveFromWeek: number | undefined
): T[] {
    if (effectiveFromWeek === undefined) {
        return [{...newValues, effectiveFromWeek: INCEPTION_WEEK}];
    }
    const next =
        previousVersions && previousVersions.length > 0
            ? [...previousVersions]
            : [{...previousSingle, effectiveFromWeek: INCEPTION_WEEK}];
    const version = {...newValues, effectiveFromWeek};
    const index = next.findIndex(v => v.effectiveFromWeek === effectiveFromWeek);
    if (index >= 0) {
        next[index] = version;
    } else {
        next.push(version);
    }
    next.sort((a, b) => a.effectiveFromWeek - b.effectiveFromWeek);
    return next;
}

const currentWeekNumber = () => getWeekInfo(new Date()).globalWeekNumber;

export const useConfigActions = ({
    stateRef,
    setState,
    authenticatedFetch,
    signalRService,
    customerId,
    baseUrl
}: ActionDeps) => {
    // Working hours config operations
    const updateWorkingHoursConfig = useCallback(
        (
            config: Partial<{
                baseHoursPerWeek: number;
                defaultHoursPerDay: number;
            }>,
            options?: ConfigWriteOptions
        ) => {
            const state = stateRef.current;
            const previousConfig = state.workingHoursConfig;
            const previousVersions = state.workingHoursConfigVersions;
            const newValues = {...state.workingHoursConfig, ...config};
            const versions = computeNextVersions(
                previousConfig,
                previousVersions,
                newValues,
                options?.effectiveFromWeek
            );
            const newCurrent =
                resolveAsOf(versions, currentWeekNumber()) ?? newValues;

            setState(prev => ({
                ...prev,
                workingHoursConfig: newCurrent,
                workingHoursConfigVersions: versions
            }));

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    workingHoursConfig: previousConfig,
                    workingHoursConfigVersions: previousVersions
                }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/working-hours-config`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify({
                            baseHoursPerWeek: newValues.baseHoursPerWeek,
                            defaultHoursPerDay: newValues.defaultHoursPerDay,
                            effectiveFromWeek:
                                options?.effectiveFromWeek ?? null
                        })
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[updateWorkingHoursConfig] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[updateWorkingHoursConfig] API call error:',
                            error
                        );
                        revert();
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // RAG threshold config operations (additional-demand amber/red + the
    // opt-in baseline amber band; null baselineAmberThreshold = band off)
    const updateRagThresholdConfig = useCallback(
        (
            config: Partial<{
                additionalDemandAmberThreshold: number;
                additionalDemandRedThreshold: number;
                baselineAmberThreshold: number | null;
            }>,
            options?: ConfigWriteOptions
        ) => {
            const state = stateRef.current;
            const previousConfig = state.ragThresholdConfig;
            const previousVersions =
                state.ragThresholdConfigVersions;
            const newValues = {
                ...state.ragThresholdConfig,
                ...config
            };
            const versions = computeNextVersions(
                previousConfig,
                previousVersions,
                newValues,
                options?.effectiveFromWeek
            );
            const newCurrent =
                resolveAsOf(versions, currentWeekNumber()) ?? newValues;

            setState(prev => ({
                ...prev,
                ragThresholdConfig: newCurrent,
                ragThresholdConfigVersions: versions
            }));

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    ragThresholdConfig: previousConfig,
                    ragThresholdConfigVersions: previousVersions
                }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/rag-threshold-config`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify({
                            additionalDemandAmberThreshold:
                                newValues.additionalDemandAmberThreshold,
                            additionalDemandRedThreshold:
                                newValues.additionalDemandRedThreshold,
                            baselineAmberThreshold:
                                newValues.baselineAmberThreshold ?? null,
                            effectiveFromWeek:
                                options?.effectiveFromWeek ?? null
                        })
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[updateRagThresholdConfig] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[updateRagThresholdConfig] API call error:',
                            error
                        );
                        revert();
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Utilisation config operations
    const updateUtilisationConfig = useCallback(
        (
            config: Partial<{
                defaultTargetPercent: number;
                includeAdditionalDemand: boolean;
            }>,
            options?: ConfigWriteOptions
        ) => {
            const state = stateRef.current;
            const previousConfig = state.utilisationConfig;
            const previousVersions = state.utilisationConfigVersions;
            const newValues = {...state.utilisationConfig, ...config};
            const versions = computeNextVersions(
                previousConfig,
                previousVersions,
                newValues,
                options?.effectiveFromWeek
            );
            const newCurrent =
                resolveAsOf(versions, currentWeekNumber()) ?? newValues;

            setState(prev => ({
                ...prev,
                utilisationConfig: newCurrent,
                utilisationConfigVersions: versions
            }));

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    utilisationConfig: previousConfig,
                    utilisationConfigVersions: previousVersions
                }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/utilisation-config`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify({
                            defaultTargetPercent:
                                newValues.defaultTargetPercent,
                            includeAdditionalDemand:
                                newValues.includeAdditionalDemand,
                            effectiveFromWeek:
                                options?.effectiveFromWeek ?? null
                        })
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[updateUtilisationConfig] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[updateUtilisationConfig] API call error:',
                            error
                        );
                        revert();
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Remove one version from a config series (e.g. a mistaken dated change).
    const deleteConfigVersion = useCallback(
        (configType: ConfigVersionType, effectiveFromWeek: number) => {
            const state = stateRef.current;
            const snapshot = {
                workingHoursConfig: state.workingHoursConfig,
                workingHoursConfigVersions: state.workingHoursConfigVersions,
                ragThresholdConfig:
                    state.ragThresholdConfig,
                ragThresholdConfigVersions:
                    state.ragThresholdConfigVersions,
                utilisationConfig: state.utilisationConfig,
                utilisationConfigVersions: state.utilisationConfigVersions
            };

            setState(prev => {
                const week = currentWeekNumber();
                switch (configType) {
                    case 'working-hours': {
                        const versions = (
                            prev.workingHoursConfigVersions ?? []
                        ).filter(v => v.effectiveFromWeek !== effectiveFromWeek);
                        return {
                            ...prev,
                            workingHoursConfigVersions: versions,
                            workingHoursConfig:
                                resolveAsOf(versions, week) ??
                                prev.workingHoursConfig
                        };
                    }
                    case 'rag-threshold': {
                        const versions = (
                            prev.ragThresholdConfigVersions ?? []
                        ).filter(v => v.effectiveFromWeek !== effectiveFromWeek);
                        return {
                            ...prev,
                            ragThresholdConfigVersions: versions,
                            ragThresholdConfig:
                                resolveAsOf(versions, week) ??
                                prev.ragThresholdConfig
                        };
                    }
                    case 'utilisation': {
                        const versions = (
                            prev.utilisationConfigVersions ?? []
                        ).filter(v => v.effectiveFromWeek !== effectiveFromWeek);
                        return {
                            ...prev,
                            utilisationConfigVersions: versions,
                            utilisationConfig:
                                resolveAsOf(versions, week) ??
                                prev.utilisationConfig
                        };
                    }
                }
            });

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/config-versions/${configType}/${effectiveFromWeek}`,
                    {
                        method: 'DELETE',
                        headers: {
                            'X-SignalR-Connection-Id': connectionId || ''
                        }
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[deleteConfigVersion] API call failed:',
                                await response.text()
                            );
                            setState(prev => ({...prev, ...snapshot}));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[deleteConfigVersion] API call error:',
                            error
                        );
                        setState(prev => ({...prev, ...snapshot}));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    return useMemo(
        () => ({
            updateWorkingHoursConfig,
            updateRagThresholdConfig,
            updateUtilisationConfig,
            deleteConfigVersion
        }),
        [
            updateWorkingHoursConfig,
            updateRagThresholdConfig,
            updateUtilisationConfig,
            deleteConfigVersion
        ]
    );
};
